import { useEffect, useRef, useState } from 'react';
import { FaQuoteLeft, FaStar } from 'react-icons/fa6';
import styles from './Testimonials.module.css';

interface Testimonial {
  name: string;
  role: string;
  quote: string;
  rating: number;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    name: 'Larissa Monteiro',
    role: 'CEO da Estilo Certo',
    avatar: 'Larissa-Monteiro.png',
    quote:
      'Meu e-commerce de roupas estava parado e o João entregou um site elegante, rápido e com catálogo fácil de atualizar. Hoje minhas coleções esgotam em poucos dias e posso lançar novidades sempre que quiser.',
    rating: 5,
  },
  {
    name: 'Bruno Carvalho',
    role: 'Diretor da Pro Pack Uniformes',
    avatar: 'Bruno-Carvalho.png',
    quote:
      'O João criou uma vitrine completa pros nossos uniformes personalizados, com formulário inteligente para orçamentos. Ganhamos credibilidade e recebemos pedidos de times do Brasil todo.',
    rating: 5,
  },
  {
    name: 'Daniela Antunes',
    role: 'Consultora em cuidados domiciliares',
    avatar: 'Daniela-Antunes.png',
    quote:
      'Além do site, o João criou um assistente inteligente que responde apenas com normas e protocolos de enfermagem. Agora tiro dúvidas clínicas com segurança e mantenho minhas diretrizes sempre em dia.',
    rating: 5,
  },
  {
    name: 'Camila Rocha',
    role: 'Fundadora do Studio Bella',
    avatar: 'Camila-Rocha.png',
    quote:
      'Eu precisava mostrar meu portfólio e fazer agendamentos online. O site ficou delicado, com fotos em destaque e botão direto pro WhatsApp. Minha agenda agora lota semanas antes.',
    rating: 5,
  },
];

type TestimonialsProps = {
  id?: string;
};

export default function Testimonials({ id }: TestimonialsProps = {}) {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const metricsRef = useRef({ step: 0 });
  const [active, setActive] = useState(0);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const cards = Array.from(slider.querySelectorAll<HTMLElement>('[data-testimonial-card]'));
    if (!cards.length) return;

    const computeStep = () => {
      if (cards.length < 2) {
        metricsRef.current.step = cards[0].clientWidth;
        return;
      }
      const first = cards[0].getBoundingClientRect();
      const second = cards[1].getBoundingClientRect();
      // prefer the distance between item left edges; fall back to measured width or bounding width
      const delta = Math.abs(second.left - first.left);
      metricsRef.current.step = delta || cards[0].clientWidth || Math.abs(first.width) || 320;
    };

    computeStep();

    let frame: number | null = null;

    const handleScroll = () => {
      const step = metricsRef.current.step;
      if (!step) return;
      const index = Math.round(slider.scrollLeft / step);
      const clamped = Math.max(0, Math.min(testimonials.length - 1, index));
      setActive((prev) => (prev === clamped ? prev : clamped));
    };

    const onScroll = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(handleScroll);
    };

    slider.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', computeStep);
    handleScroll();

    return () => {
      slider.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', computeStep);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const step = () => {
      if (!metricsRef.current.step) return;
      slider.scrollTo({
        left: metricsRef.current.step * ((active + 1) % testimonials.length),
        behavior: 'smooth',
      });
    };

    const interval = window.setInterval(step, 6400);

    return () => {
      window.clearInterval(interval);
    };
  }, [active]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    let pointerDown = false;
    let startX = 0;
    let scrollStart = 0;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerDown = true;
      startX = event.clientX;
      scrollStart = slider.scrollLeft;
      slider.classList.add(styles.isDragging);
      slider.setPointerCapture?.(event.pointerId);
      slider.focus({ preventScroll: true });
      event.preventDefault();
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!pointerDown) return;
      const deltaX = event.clientX - startX;
      slider.scrollLeft = scrollStart - deltaX;
      event.preventDefault();
    };

    const endDrag = (event: PointerEvent) => {
      if (!pointerDown) return;
      pointerDown = false;
      slider.classList.remove(styles.isDragging);
      slider.releasePointerCapture?.(event.pointerId);
      event.preventDefault();
    };

    slider.addEventListener('pointerdown', handlePointerDown);
    slider.addEventListener('pointermove', handlePointerMove);
    slider.addEventListener('pointerup', endDrag);
    slider.addEventListener('pointerleave', endDrag);
    slider.addEventListener('pointercancel', endDrag);

    return () => {
      slider.removeEventListener('pointerdown', handlePointerDown);
      slider.removeEventListener('pointermove', handlePointerMove);
      slider.removeEventListener('pointerup', endDrag);
      slider.removeEventListener('pointerleave', endDrag);
      slider.removeEventListener('pointercancel', endDrag);
    };
  }, []);

  return (
    <section id={id} className={styles.wrapper} aria-labelledby="testimonials-heading">
      <div className={styles.container}>
        <header className={styles.header}>
          <span className={styles.subtitle}>O que meus clientes dizem</span>
          <h2 id="testimonials-heading">
            Aqui, a <strong>satisfação dos clientes</strong> é a minha melhor vitrine.
          </h2>
        </header>

        <div className={styles.slider} ref={sliderRef} tabIndex={0} role="list">
          {testimonials.map(({ name, role, quote, rating, avatar }) => (
            <article
              key={name}
              className={styles.card}
              data-testimonial-card
              role="listitem"
              aria-label={`${name}, ${role}`}
            >
              <div className={styles.cardHeader}>
                <div className={styles.avatar} aria-hidden="true">
                  <img src={`/${avatar}`} alt={name} loading="lazy" />
                </div>
                <div>
                  <strong>{name}</strong>
                  <span>{role}</span>
                </div>
              </div>

              <div className={styles.stars} aria-hidden="true">
                {Array.from({ length: rating }).map((_, starIndex) => (
                  <FaStar key={starIndex} />
                ))}
              </div>

              <p className={styles.quote}>
                <FaQuoteLeft aria-hidden="true" />
                {quote}
              </p>
            </article>
          ))}
        </div>

        <div className={styles.dots} role="presentation">
          {testimonials.map((_, index) => (
            <span
              key={`dot-${index}`}
              className={index === active ? `${styles.dot} ${styles.dotActive}` : styles.dot}
              aria-hidden="true"
            />
          ))}
        </div>

        <a
          className={styles.ctaButton}
          href="https://wa.me/5535999564940?text=Ol%C3%A1%21%20Acabei%20de%20ler%20os%20depoimentos%20no%20site%20e%20quero%20ser%20um%20cliente."
          target="_blank"
          rel="noreferrer"
        >
          Quero ser um cliente
        </a>
      </div>
    </section>
  );
}
